import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Message } from '../ChatUpdated/ChatUpdated.styled';
import { DownWithBG, UpIcon } from '../QPlayerSVGElement';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { useSelector } from 'react-redux';

const Container = styled.div`
	height: fit-content;
	width: 100%;
	display: flex;
	flex-direction: column;
	overflow-y: auto;
	overflow-x: hidden !important;
	font: normal 1em/1.33333 !important;
	font-family: inherit;
	pointer-events: none;
	position: relative;

	${({ isExpanded }) =>
		!isExpanded &&
		css`
			-webkit-mask-image: linear-gradient(to top, transparent 0%, black 0%, black 77%, transparent 100%);
			mask-image: linear-gradient(to top, transparent 0%, black 0%, black 77%, transparent 100%);
		`}

	@media screen and (max-width: 992px) and (orientation: portrait) and (hover: none) and (pointer: coarse) {
		max-height: 80%;
		height: fit-content;
		width: 100%;
		display: block;
		overflow-y: auto !important;
		overflow-x: hidden !important;
		position: relative;
	}
`;

const ExpandButton = styled.div`
	background: rgba(0, 0, 0, 0.647);
	display: inline-flex;
	justify-content: center;
	height: 40px;
	align-items: center;
	width: 40px;
	justify-self: self-end;
	align-self: flex-end;
	pointer-events: auto;
	cursor: pointer;
	border-radius: 50%;
	z-index: 1;
	position: absolute;
	right: 0;

	&:disabled {
		cursor: default;
	}
`;

const ChatTop = ({
	showScrollOnChat,
	messageContainerRef,
	messages,
	settings,
	onRefreshButtonClick,
	closeIncomingMessage,
	questions,
	onUpvoteButtonClick,
}) => {
	const { t } = useTranslation();
	const { accountId, isChatActivated } = useSelector((state) => state.configurationReducer);

	const [isExpanded, setIsExpanded] = useState(false);
	const [buttonPos, setButtonPos] = useState(0);
	const [isAllowToRefresh, setAllowToRefresh] = useState(true);

	useEffect(() => {
		const poll = document.getElementsByClassName('poll__wrapper')[0];
		const qna = document.getElementsByClassName('qna__wrapper')[0];
		const pinnedMess = document.getElementsByClassName('chat-message--pinned')[0];

		let pos = isChatActivated ? 75 : 0;
		if (poll) {
			pos += poll.getBoundingClientRect().height;
		}
		if (qna) {
			pos += qna.getBoundingClientRect().height;
		}
		if (pinnedMess) {
			pos += pinnedMess.getBoundingClientRect().height;
		}

		pos += 5; // So it will not overlap with ChatNotice

		if (pos !== buttonPos) {
			setButtonPos(pos);
		}
	});

	const onRefreshClick = useCallback(() => {
		if (!isAllowToRefresh || closeIncomingMessage) {
			return;
		}

		setAllowToRefresh(false);
		onRefreshButtonClick();
		const allowToRefreshTimeout = setTimeout(() => {
			clearTimeout(allowToRefreshTimeout);
			setAllowToRefresh(true);
		}, 3000);
	}, [closeIncomingMessage, isAllowToRefresh, onRefreshButtonClick]);

	const messToShow = useMemo(() => {
		let mess = [...(messages ?? []), ...(questions ?? [])];
		mess = mess.sort((messA, messB) => {
			return new Date(messA.created) - new Date(messB.created);
		});
		return mess;
	}, [messages, questions]);

	const isVoted = useCallback(
		(id) => {
			const upvotedQuestions = Cookies.get(`chat_upvoted_${accountId}`);
			return (upvotedQuestions ?? '').includes(id);
		},
		[accountId]
	);

	return (
		<>
			<ExpandButton onClick={() => setIsExpanded((prev) => !prev)} style={{ bottom: buttonPos + 'px' }}>
				{isExpanded ? <DownWithBG /> : <UpIcon color="#fff" />}
			</ExpandButton>
			<ExpandButton
				disabled={!isAllowToRefresh || closeIncomingMessage}
				onClick={!isAllowToRefresh || closeIncomingMessage ? undefined : onRefreshClick}
				style={{ right: '50px', bottom: buttonPos + 'px' }}
				title={t('REFRESH_CHAT')}
			>
				<RefreshIcon htmlColor={!isAllowToRefresh || closeIncomingMessage ? 'gray' : '#fff'} style={{ width: '.75em', height: '.75em' }} />
			</ExpandButton>

			<Container
				isExpanded={isExpanded}
				showScrollOnChat={showScrollOnChat}
				ref={messageContainerRef}
				style={{ height: isExpanded ? '60%' : '10%', opacity: isExpanded ? '1' : '0.7', pointerEvents: 'all' }}
			>
				{[...(messToShow ?? {})].map(({ id, sender: { admin, name }, recipient, question, message, blocked, votes, reply, qnaId }, i) => {
					const replyTo =
						recipient?.replyMessageId || recipient?.replyQuestionId
							? messToShow.find((m) => m.id === recipient?.replyMessageId || m.id === recipient?.replyQuestionId)
							: undefined;

					return (
						!blocked && (
							<Message
								primaryColor={settings?.playerStyling?.primaryColor ?? '#126cfc'}
								key={id + i}
								id={id}
								isAdmin={admin}
								name={name}
								content={qnaId ? (replyTo ? reply : question) : message?.text}
								replyContent={replyTo?.qnaId ? replyTo?.question : replyTo?.message?.text}
								isQuestion={!!(question && !reply)}
								onUpvoteButtonClick={(e) => onUpvoteButtonClick?.(e, id, qnaId)}
								votes={votes ?? 0}
								isVoted={isVoted(id)}
								isContainerExpanded={isExpanded}
							/>
						)
					);
				})}
			</Container>
		</>
	);
};

export { ChatTop };
